import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { Avatar } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { FormCard } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import { StockRecord } from '../../types'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'

type DisposalDetailContentProps = {
  record: StockRecord
  medicationName: string
}

export const DisposalDetailContent = ({
  record
}: DisposalDetailContentProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const description = Array.isArray(record.notes)
    ? record.notes.join(', ')
    : record.notes || ''
  const expectedStock = record.expectedStock ?? 0
  const previousExpectedStock = expectedStock + Math.abs(record.quantityChange)
  const disposedStock = Math.abs(record.quantityChange)
  const stockUnit = record.unit || ''

  const actionedByName = record.disposedBy
    ? typeof record.disposedBy.name === 'object'
      ? `${record.disposedBy.name?.first || ''} ${record.disposedBy.name?.last || ''}`.trim()
      : record.disposedBy.name || ''
    : ''
  const actionedDateFormatted = record.actionTime
    ? dayjs(record.actionTime).format('DD-MM-YYYY')
    : '-'

  const timeLogged = record.date
    ? dayjs(record.date).format('DD-MM-YYYY HH:mm')
    : '-'
  const carerName =
    typeof record.recordedBy?.name === 'object'
      ? `${record.recordedBy?.name?.first || ''} ${record.recordedBy?.name?.last || ''}`.trim()
      : record.recordedBy?.name || ''

  return (
    <SectionContainer>
      <FormCard level={2}>
        <InfoRow>
          <FullWidthSection>
            <StyledLabel level={2}>{t('DESCRIPTION')}</StyledLabel>
            <Text>{description || '-'}</Text>
          </FullWidthSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('PREVIOUS_EXPECTED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {previousExpectedStock !== undefined
                ? `${previousExpectedStock} ${stockUnit}`
                : '-'}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('DISPOSED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {disposedStock !== undefined
                ? `${disposedStock} ${stockUnit}`
                : '-'}
            </Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('EXPECTED_STOCK')}</StyledLabel>
            <Text weight="bold">
              {expectedStock !== undefined
                ? `${expectedStock} ${stockUnit}`
                : '-'}
            </Text>
          </InfoSection>
        </InfoRow>
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('ACTIONED_BY')}</StyledLabel>
            {record.disposedBy ? (
              <ActionedByContainer>
                <Avatar
                  url={(record.disposedBy as any).picture as unknown as string}
                  size={16}
                />
                <Text weight="bold">{actionedByName || '-'}</Text>
              </ActionedByContainer>
            ) : (
              <Text weight="bold">-</Text>
            )}
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('ACTIONED_DATE')}</StyledLabel>
            <Text weight="bold">{actionedDateFormatted || '-'}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('DISPOSAL_METHOD')}</StyledLabel>
            <Text weight="bold">{record.disposalMethod || '-'}</Text>
          </InfoSection>
        </InfoRow>
        <RowDivider />
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('TIME_RECORDED')}</StyledLabel>
            <Text>{timeLogged}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECORDED_BY')}</StyledLabel>
            <RecordedByContainer>
              <Avatar
                url={record.recordedBy?.picture as unknown as string}
                size={16}
              />
              <Text>{carerName || '-'}</Text>
            </RecordedByContainer>
          </InfoSection>
          {record.witnessed &&
            record.witnesses &&
            record.witnesses.length > 0 && (
              <InfoSection>
                <StyledLabel level={2}>{t('WITNESSED_BY')}</StyledLabel>
                <WitnessesContainer>
                  {record.witnesses.map((witness: any, index: number) => {
                    return (
                      <WitnessItem key={witness.userId || index}>
                        <Avatar
                          url={witness.picture as unknown as string}
                          size={16}
                        />
                        <Text weight="bold">{witness.name || '-'}</Text>
                      </WitnessItem>
                    )
                  })}
                </WitnessesContainer>
              </InfoSection>
            )}
        </InfoRow>
      </FormCard>
    </SectionContainer>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 184px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ActionedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RowDivider = styled(Divider)`
  margin: -8px 0;
`

const RecordedByContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const WitnessesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const WitnessItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
